import React, { useState, useEffect } from 'react'
import {
  Card,
  CardContent,
  Typography,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Divider,
  Grid,
  Box,
  Chip,
  FormHelperText,
  Collapse,
  CircularProgress,
} from '@mui/material'
import NewCertFields from './NewCertFields'
import { certificateApi } from '../../utils/api'

const ContactCertInfoStep = ({
  formData,
  setFormData,
  isComplete,
  isVisible,
  checkVenafiCommonName,
  checkCommonNameOnboarded,
  commonNameOnboarded,
  venafiLookup,
  selectVenafiMatch,
  addSanEntry,
  updateSanEntry,
  removeSanEntry,
  stepNumber = 4,
}) => {
  const isPreExisting = formData.preExisting === 'true'

  const [itsmGroups, setItsmGroups] = useState([])
  const [loadingItsmGroups, setLoadingItsmGroups] = useState(false)

  useEffect(() => {
    setLoadingItsmGroups(true)
    certificateApi.getItsmGroups()
      .then(res => setItsmGroups(res.data.groups || []))
      .catch(() => setItsmGroups([]))
      .finally(() => setLoadingItsmGroups(false))
  }, [])

  // Validate that a string looks like one or more email addresses
  const isValidEmail = (str) => {
    if (!str) return false
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    // Support comma-separated list
    return str.split(',').map(s => s.trim()).every(s => emailRe.test(s))
  }

  const venafiContactEmail = venafiLookup?.selectedMatch?.contact_email
  const venafiSuppliedValidEmail = isValidEmail(venafiContactEmail)
  // Email field is editable when: new cert (always editable for UX safety), OR
  // pre-existing but Venafi didn't return a valid email
  const emailEditable = !isPreExisting || (isPreExisting && !!venafiLookup?.selectedMatch && !venafiSuppliedValidEmail)

  return (
    <Collapse in={isVisible}>
      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={isComplete ? "✓" : String(stepNumber)} 
              color={isComplete ? "success" : "primary"} 
              size="small" 
            />
            Contact and Certificate Information
          </Typography>
          <Divider sx={{ my: 2 }} />
          
          <Grid container spacing={3}>
            {/* Email - shared */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Certificate Requestor Email ID *"
                type="email"
                value={formData.emailId}
                placeholder="requestor@broadridge.com, other@broadridge.com"
                helperText={
                  isPreExisting && venafiLookup?.selectedMatch
                    ? venafiSuppliedValidEmail
                      ? 'Auto-populated from the Venafi certificate record (Contact Email Application Team)'
                      : venafiContactEmail
                        ? `Venafi returned "${venafiContactEmail}" which is not a valid email. Please enter the requestor email manually. Multiple emails can be comma-separated.`
                        : 'No requestor email found in Venafi. Please enter it manually. Multiple emails can be comma-separated.'
                    : 'Auto-filled from your login credentials'
                }
                onChange={emailEditable ? (e) => setFormData({ ...formData, emailId: e.target.value }) : undefined}
                InputProps={{
                  readOnly: !emailEditable,
                }}
                disabled={!emailEditable && !venafiSuppliedValidEmail}
                error={isPreExisting && venafiLookup?.selectedMatch && !venafiSuppliedValidEmail && !formData.emailId}
              />
            </Grid>

            {/* ITSM Group - shared for both new and pre-existing certs */}
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>ITSM Group of the team updating the cert *</InputLabel>
                <Select
                  value={formData.itsmGroup}
                  onChange={(e) => setFormData({ ...formData, itsmGroup: e.target.value })}
                  label="ITSM Group of the team updating the cert *"
                  disabled={loadingItsmGroups}
                  startAdornment={
                    loadingItsmGroups
                      ? <CircularProgress size={18} sx={{ mr: 1 }} />
                      : null
                  }
                >
                  {itsmGroups.map((group) => (
                    <MenuItem key={group} value={group}>{group}</MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {loadingItsmGroups
                    ? 'Loading groups…'
                    : 'Select the ITSM assignment group responsible for updating this certificate'}
                </FormHelperText>
              </FormControl>
            </Grid>

            {/* Common Name - only for new certs (pre-existing enters CN in Step 2) */}
            {!isPreExisting && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Common Name *"
                  value={formData.commonName}
                  onChange={(e) => setFormData({ ...formData, commonName: e.target.value })}
                  onBlur={() => checkCommonNameOnboarded(formData.commonName)}
                  helperText={
                    commonNameOnboarded?.onboarded
                      ? commonNameOnboarded.message
                      : 'e.g., app.broadridge.com or app.broadridge.net'
                  }
                  error={!!commonNameOnboarded?.onboarded}
                  required
                />
              </Grid>
            )}

            {/* Pre-existing cert: show read-only Common Name for context */}
            {isPreExisting && formData.commonName && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Common Name"
                  value={formData.commonName}
                  InputProps={{ readOnly: true }}
                  helperText="Selected in the Venafi Certificate Lookup step"
                  disabled
                />
              </Grid>
            )}

            {/* New cert specific fields */}
            {formData.preExisting === 'false' && (
              <NewCertFields
                formData={formData}
                setFormData={setFormData}
              />
            )}

            {/* Dependency Present - only for new certs */}
            {formData.preExisting !== 'true' && (
              <>
                <Grid item xs={12}>
                  <FormControl component="fieldset">
                    <FormLabel>Dependency Present? *</FormLabel>
                    <RadioGroup
                      row
                      value={formData.dependencyPresent}
                      onChange={(e) => setFormData({ ...formData, dependencyPresent: e.target.value })}
                    >
                      <FormControlLabel value="true" control={<Radio />} label="True" />
                      <FormControlLabel value="false" control={<Radio />} label="False" />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                {formData.dependencyPresent === 'true' && (
                  <>
                    {formData.dependencies.map((dependency, index) => (
                      <Grid item xs={12} key={index}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                          <TextField
                            fullWidth
                            label={`Dependency ${index + 1}`}
                            value={dependency}
                            onChange={(e) => {
                              const newDependencies = [...formData.dependencies]
                              newDependencies[index] = e.target.value
                              setFormData({ ...formData, dependencies: newDependencies })
                            }}
                            multiline
                          />
                          {formData.dependencies.length > 1 && (
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              onClick={() => {
                                const newDependencies = formData.dependencies.filter((_, i) => i !== index)
                                setFormData({ ...formData, dependencies: newDependencies })
                              }}
                              sx={{ mt: 1, minWidth: 'auto' }}
                            >
                              Remove
                            </Button>
                          )}
                        </Box>
                      </Grid>
                    ))}
                    <Grid item xs={12}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setFormData({ ...formData, dependencies: [...formData.dependencies, ''] })
                        }}
                        startIcon={<span>+</span>}
                      >
                        Add Another Dependency
                      </Button>
                    </Grid>
                  </>
                )}
              </>
            )}

            {/* Venafi Policy Path - shared */}
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Venafi Policy Path *</InputLabel>
                <Select
                  value={formData.venafiPolicyBase}
                  onChange={(e) => setFormData({ ...formData, venafiPolicyBase: e.target.value })}
                  label="Venafi Policy Path *"
                >
                  {formData.environment === 'prd' ? (
                    [
                      <MenuItem key="prod-digicert" value="prod-digicert">\\VED\\Policy\\Production\\BRCC DigiCert Standard CA\\Automation</MenuItem>,
                      <MenuItem key="prod-entrust" value="prod-entrust">\\VED\\Policy\\Production\\BRCC Entrust Private Gateway CA\\Automation</MenuItem>
                    ]
                  ) : (
                    [
                      <MenuItem key="nonprod-digicert" value="nonprod-digicert">\\VED\\Policy\\Non-Production\\BRCC DigiCert Standard CA\\Automation</MenuItem>,
                      <MenuItem key="nonprod-entrust" value="nonprod-entrust">\\VED\\Policy\\Non-Production\\BRCC Entrust Private Gateway CA\\Automation</MenuItem>
                    ]
                  )}
                </Select>
                <FormHelperText>Select the Venafi policy path for certificate creation ({formData.environment === 'prd' ? 'Production' : 'Non-Production'})</FormHelperText>
              </FormControl>
            </Grid>

            {/* Venafi Path - shared */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Venafi Path (after Automation folder)"
                value={formData.venafiPath}
                onChange={(e) => setFormData({ ...formData, venafiPath: e.target.value })}
                placeholder="E.g., \..\DEP"
                helperText={formData.preExisting === 'true' && venafiLookup.selectedMatch 
                  ? 'Auto-populated from selected Venafi certificate'
                  : 'Optional path in Venafi after Automation folder'
                }
                InputProps={{
                  readOnly: formData.preExisting === 'true' && venafiLookup.selectedMatch
                }}
              />
            </Grid>

            {/* SAN Entries - shared */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                SAN Entries
                {formData.preExisting === 'true'
                  ? (venafiLookup.selectedMatch ? ' (from Venafi)' : ' (auto-populated after retrieval)')
                  : ' (Optional)'
                }
              </Typography>
              {formData.sanEntries.map((san, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    value={san}
                    onChange={(e) => {
                      if (formData.preExisting !== 'true') {
                        updateSanEntry(index, e.target.value)
                      }
                    }}
                    placeholder="san.example.com"
                    disabled={formData.preExisting === 'true' && !venafiLookup.selectedMatch}
                    InputProps={{
                      readOnly: formData.preExisting === 'true' && !!venafiLookup.selectedMatch
                    }}
                  />
                  {formData.preExisting !== 'true' && (
                    <>
                      {index === formData.sanEntries.length - 1 && (
                        <Button onClick={addSanEntry} variant="outlined" size="small">+</Button>
                      )}
                      {formData.sanEntries.length > 1 && (
                        <Button onClick={() => removeSanEntry(index)} variant="outlined" size="small" color="error">−</Button>
                      )}
                    </>
                  )}
                </Box>
              ))}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Collapse>
  )
}

export default ContactCertInfoStep
